/**
 * OAuth popup and redirect handler
 */

import type {
  Grant,
  GrantDelegation,
  AlterError,
  ConnectCompletion,
  ConnectSessionKind,
  ManagedSecretOutcome,
} from "../types";
import { DEFAULT_ALTER_HOST } from "../core/config";
import { shouldUseRedirectFlow } from "../utils/mobile";
import { parseConnectMessage, UUID_PATTERN } from "./messages";

interface OAuthHandlerOptions {
  baseURL: string;
  onSuccess: (grants: Grant[], completion: ConnectCompletion) => void;
  onError: (error: AlterError) => void;
  onCancel: () => void;
  /**
   * A non-terminal notice from the hosted flow. MUST NOT settle or close the
   * flow — the session is still live and the user can still complete it.
   */
  onNotice?: (eventName: string, metadata: Record<string, unknown>) => void;
  popupWidth?: number;
  popupHeight?: number;
  debug?: boolean;
  /** Expected origin for postMessage validation. Derived from the OAuth URL. */
  expectedOrigin?: string;
  /**
   * The session's kind, from the token the widget was given. Every completion
   * carries it, and a redirect flow stores it for the return.
   */
  kind?: ConnectSessionKind;
}

/**
 * Parse an operation string into a valid Grant['operation'] value
 */
function parseOperation(value: string | null | undefined): Grant["operation"] {
  if (value === "reauth") return "reauth";
  return "creation";
}

/**
 * Parse a status string into a valid Grant['status'] value
 */
function parseStatus(value: string | null | undefined): Grant["status"] {
  if (value === "pending") return "pending";
  if (value === "error") return "error";
  return "active";
}

/** sessionStorage key holding the in-flight redirect-flow state. */
const REDIRECT_STATE_KEY = "alter_oauth_state";

/**
 * Query parameter carrying this flow's nonce OUT to the Connect UI and back on
 * the callback. It is a binding value, not a secret: its only job is to prove
 * that the callback parameters belong to the redirect this SDK instance
 * started.
 */
const REDIRECT_NONCE_PARAM = "alter_connect_nonce";

/**
 * Every query parameter this SDK's redirect callback puts on the return URL.
 *
 * Stripped together when the callback lands on a page OTHER than the one that
 * launched the flow — leaving any of them behind means a refresh or a shared
 * link re-triggers the return handling against a spent flow. Kept beside
 * `REDIRECT_NONCE_PARAM` and the reader in `checkOAuthReturn` so a new
 * parameter is added in one obvious place.
 */
const CALLBACK_PARAMS = [
  "alter_connect_success",
  "alter_connect_error",
  REDIRECT_NONCE_PARAM,
  "grant_id",
  "provider",
  "provider_name",
  "account_identifier",
  "timestamp",
  "operation",
  "scopes",
  "status",
  "error_code",
  // The backend's mobile return URL emits ``error_description`` (see
  // callback_error.html); ``error_message`` was never emitted and its old
  // entry left the real param in the address bar after a denial.
  "error_description",
  // The first grant's delegation to the session's agent (connect_success.html).
  "delegated_agent_id",
  // A managed-secret session's outcome (its widget redirect).
  "managed_secret_path",
  "managed_secret_credential_kind",
  "managed_secret_preflight_status",
  "managed_secret_preflight_message",
] as const;

/**
 * The parameters the page writes only together with `delegated_agent_id`.
 * Without it they are not the callback's: a session with no agent writes none
 * of them, so a `delegable` on the URL then belongs to the application's own
 * return URL and is left alone, both when reading the outcome and when
 * stripping the callback from the address bar.
 */
const DELEGATION_COMPANION_PARAMS = [
  "delegable",
  "delegation_policy_refused",
  // The delegation's own grant id (a managed-secret session's agent).
  "delegation_grant_id",
] as const;

/**
 * The redirect callback's delegation, `null` when the session delegated to no
 * agent (no `delegated_agent_id`), or `"invalid"` when `delegated_agent_id`
 * comes with partial or malformed companions: an agent id that is not a UUID,
 * a `delegable` that is not the literal `true` / `false`, a
 * `delegation_policy_refused` that is present but not one of them, or a
 * `delegation_grant_id` that is present but not a UUID.
 */
function parseRedirectDelegation(
  urlParams: URLSearchParams,
): GrantDelegation | null | "invalid" {
  const agentId = urlParams.get("delegated_agent_id");
  // No agent: any `delegable` / `delegation_policy_refused` here is the
  // application's own, not part of this outcome.
  if (agentId === null) return null;
  const delegable = urlParams.get("delegable");
  const policyRefused = urlParams.get("delegation_policy_refused");
  const delegationGrantId = urlParams.get("delegation_grant_id");
  const isLiteralBool = (value: string | null): value is "true" | "false" =>
    value === "true" || value === "false";
  if (
    !UUID_PATTERN.test(agentId) ||
    !isLiteralBool(delegable) ||
    (policyRefused !== null && !isLiteralBool(policyRefused)) ||
    (delegationGrantId !== null && !UUID_PATTERN.test(delegationGrantId))
  ) {
    return "invalid";
  }
  const delegation: GrantDelegation = {
    agent_id: agentId,
    delegable: delegable === "true",
    policy_refused: policyRefused === "true",
  };
  if (delegationGrantId !== null) delegation.grant_id = delegationGrantId;
  return delegation;
}

/** How long a stored redirect-flow state stays usable (5 minutes). */
const REDIRECT_STATE_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * The redirect-flow state persisted across the full-page OAuth navigation.
 *
 * Validated field-by-field on read ({@link parseRedirectFlowState}) rather than
 * trusted from `JSON.parse`: sessionStorage is same-origin-writable by any
 * script on the page, and `JSON.parse` is typed `any`, so an unvalidated read
 * silently propagates whatever shape it finds.
 */
interface RedirectFlowState {
  timestamp: number;
  returnUrl: string;
  nonce: string;
  /** The flow's session kind; absent from a state an older SDK wrote. */
  kind: ConnectSessionKind;
}

/**
 * Parse and VALIDATE a stored redirect-flow state, or return null.
 *
 * Every field is type-checked. The timestamp check in particular is
 * security-relevant: the previous `JSON.parse` result was `any`, so a
 * non-numeric `timestamp` made `Date.now() - state.timestamp` evaluate to
 * `NaN`, and `NaN > MAX_AGE` is `false` — the freshness guard FAILED OPEN on
 * exactly the corrupt or forged state it existed to reject.
 */
function parseRedirectFlowState(rawJson: string): RedirectFlowState | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { timestamp, returnUrl, nonce, kind } = parsed as Record<
    string,
    unknown
  >;
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) return null;
  if (typeof returnUrl !== "string" || returnUrl.length === 0) return null;
  if (typeof nonce !== "string" || nonce.length === 0) return null;
  if (kind !== undefined && kind !== "oauth" && kind !== "managed_secret") {
    return null;
  }
  return { timestamp, returnUrl, nonce, kind: kind ?? "oauth" };
}

/**
 * A managed-secret redirect return's outcome, or `null` when its
 * `managed_secret_*` parameters are missing or malformed: `path` and
 * `credential_kind` are required, and `preflight_message` only rides with a
 * `preflight_status`.
 */
function parseRedirectManagedSecret(
  urlParams: URLSearchParams,
): ManagedSecretOutcome | null {
  const path = urlParams.get("managed_secret_path");
  const credentialKind = urlParams.get("managed_secret_credential_kind");
  const status = urlParams.get("managed_secret_preflight_status");
  const message = urlParams.get("managed_secret_preflight_message");
  if (path !== "fast_path" && path !== "key_entry" && path !== "repair") {
    return null;
  }
  if (credentialKind !== "developer" && credentialKind !== "end_user") {
    return null;
  }
  if (status === null) {
    return message === null ? { path, credentialKind, preflight: null } : null;
  }
  if (
    status !== "passed" &&
    status !== "inconclusive" &&
    status !== "skipped"
  ) {
    return null;
  }
  return { path, credentialKind, preflight: { status, message } };
}

/**
 * Generate the per-flow binding nonce, or null when the environment offers no
 * cryptographic randomness (a non-secure context, or a very old browser).
 *
 * Returning null makes the caller FAIL CLOSED — a redirect flow whose callback
 * cannot be bound is not started at all, rather than started with a guessable
 * or constant value that would read as a check while enforcing nothing.
 */
function generateFlowNonce(): string | null {
  const webCrypto: Crypto | undefined = globalThis.crypto;
  if (!webCrypto) return null;
  if (typeof webCrypto.randomUUID === "function") {
    return webCrypto.randomUUID();
  }
  if (typeof webCrypto.getRandomValues === "function") {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  }
  return null;
}

/** True when this page URL carries this SDK's redirect-callback parameters. */
function hasCallbackParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("alter_connect_success") !== null ||
    params.get("alter_connect_error") !== null
  );
}

/**
 * Clean up OAuth redirect state and tidy the address bar.
 *
 * `returnUrl` is the URL of the page that STARTED the flow. Restoring it
 * wholesale is only correct when the callback came back to that same page, and
 * a `return_url` pointing at a different path on the same origin is perfectly
 * legal — the backend accepts it. In that case rewriting to the launch path put
 * the return page's rendered DOM under an address that does not describe it:
 * deep links, refreshes and analytics all then disagree with what the user is
 * looking at.
 *
 * So the launch URL is restored only for a same-path return. Otherwise the
 * callback parameters are stripped from the CURRENT URL, which is the actual
 * goal — get the noisy `?alter_connect_success=…` out of the address bar —
 * without claiming the user is somewhere they are not.
 */
function cleanupRedirectState(returnUrl: string): void {
  sessionStorage.removeItem(REDIRECT_STATE_KEY);
  try {
    const launched = new URL(returnUrl);
    if (launched.pathname === window.location.pathname) {
      window.history.replaceState(
        {},
        document.title,
        launched.pathname + launched.search + launched.hash,
      );
      return;
    }
  } catch {
    // Malformed stored URL — fall through to stripping the current one.
  }
  stripCallbackParams();
}

/** Remove every redirect outcome field from the current URL, preserving state. */
function stripCallbackParams(): void {
  const current = new URL(window.location.href);
  const delegated = current.searchParams.has("delegated_agent_id");
  for (const param of CALLBACK_PARAMS) current.searchParams.delete(param);
  if (delegated) {
    for (const param of DELEGATION_COMPANION_PARAMS) {
      current.searchParams.delete(param);
    }
  }
  window.history.replaceState(
    {},
    document.title,
    current.pathname + current.search + current.hash,
  );
}

/** How often the handler checks whether the popup has closed. */
export const POPUP_POLL_INTERVAL_MS = 500;

/** How long a closed popup may still deliver its outcome before the flow is
 *  reported as cancelled (see ``startPolling``). Long enough for a message
 *  posted just before the window closed to be dispatched; short enough that a
 *  real cancel still feels immediate. */
export const POPUP_CLOSED_GRACE_MS = 300;

/**
 * Handles OAuth flow in popup window
 */
export class OAuthHandler {
  private popup: Window | null = null;
  private pollInterval: number | null = null;
  private closedGraceTimer: number | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private options: Required<OAuthHandlerOptions>;
  private expectedOrigin: string;
  private settled: boolean = false;

  constructor(options: OAuthHandlerOptions) {
    this.options = {
      baseURL: options.baseURL,
      onSuccess: options.onSuccess,
      onError: options.onError,
      onCancel: options.onCancel,
      // Optional to callers; normalized to a no-op so the message listener can
      // dispatch a notice unconditionally.
      onNotice: options.onNotice || ((): void => {}),
      popupWidth: options.popupWidth || 500,
      popupHeight: options.popupHeight || 700,
      debug: options.debug || false,
      expectedOrigin: options.expectedOrigin || "",
      kind: options.kind ?? "oauth",
    };

    // Extract origin from baseURL for postMessage validation
    try {
      this.expectedOrigin = new URL(options.baseURL).origin;
    } catch {
      throw new Error(
        `Invalid baseURL: "${options.baseURL}". Must be a full URL with protocol (e.g., "${DEFAULT_ALTER_HOST}").`,
      );
    }
  }

  /**
   * Start OAuth flow (automatically chooses popup or redirect based on device)
   *
   * @returns the route taken, or `null` when the flow was refused before it
   *   started (the error callback has already fired). Every refusal returns
   *   `null`: a blocked popup, a redirect the browser could not bind, an
   *   unparseable URL. The caller relies on this to record `connect_opened`
   *   only for a flow that actually opened.
   */
  startOAuth(url: string): "popup" | "redirect" | null {
    if (!this.deriveExpectedOrigin(url)) {
      return null;
    }

    if (shouldUseRedirectFlow()) {
      this.log("Using redirect flow for mobile device");
      return this.startRedirectFlow(url) ? "redirect" : null;
    }
    this.log("Using popup flow for desktop");
    return this.openPopup(url) ? "popup" : null;
  }

  /**
   * Derive the expected origin from the OAuth URL if not explicitly set.
   * SECURITY: fail-closed — if the origin cannot be determined, abort the flow.
   */
  private deriveExpectedOrigin(url: string): boolean {
    if (this.options.expectedOrigin) {
      return true;
    }
    try {
      this.options.expectedOrigin = new URL(url).origin;
      this.log("Derived expected origin:", this.options.expectedOrigin);
      return true;
    } catch {
      this.log("Failed to parse OAuth URL for origin validation:", url);
      this.options.onError({
        code: "invalid_oauth_url",
        message:
          "Failed to determine origin from OAuth URL. Cannot proceed securely.",
      });
      return false;
    }
  }

  /**
   * Open the provider window regardless of device, reporting a blocked
   * window as `false` instead of an error: the inline direct flow falls back
   * to its tile in that case. Everything else (origin check, close polling,
   * the message listener) is the popup flow's.
   */
  tryOpenWindow(url: string): boolean {
    if (!this.deriveExpectedOrigin(url)) {
      return false;
    }
    const popup = this.createPopup(url);
    if (!popup) {
      return false;
    }
    this.popup = popup;
    this.startPolling();
    this.setupMessageListener();
    return true;
  }

  /** Bring the provider window to the front, if it is still open. */
  focusWindow(): void {
    if (this.popup && !this.popup.closed) {
      this.popup.focus();
    }
  }

  /**
   * Start OAuth redirect flow (for mobile)
   * Saves state and redirects the entire page
   *
   * The state carries a per-flow nonce which is ALSO sent to the Connect UI as
   * a query parameter, so the callback can echo it back and
   * {@link OAuthHandler.checkOAuthReturn} can prove the callback parameters
   * belong to this flow. It goes in the QUERY, never the fragment — a fragment
   * is never transmitted to the server, so the callback could not echo it.
   *
   * @returns `true` when the navigation was issued, `false` when the flow was
   *   refused (the error callback has already fired).
   */
  private startRedirectFlow(url: string): boolean {
    this.log("Starting redirect flow:", url);

    const nonce = generateFlowNonce();
    if (nonce === null) {
      this.log("No cryptographic randomness available; refusing redirect flow");
      this.options.onError({
        code: "redirect_error",
        message:
          "Failed to start OAuth flow: this browser provides no secure random " +
          "source, so the OAuth callback could not be bound to this flow",
      });
      return false;
    }

    let redirectTarget: string;
    try {
      const parsed = new URL(url);
      parsed.searchParams.set(REDIRECT_NONCE_PARAM, nonce);
      redirectTarget = parsed.toString();
    } catch {
      this.log("Failed to parse OAuth URL for redirect flow:", url);
      this.options.onError({
        code: "invalid_oauth_url",
        message:
          "Failed to start OAuth flow: the OAuth URL is not a valid absolute URL",
      });
      return false;
    }

    // Save state to sessionStorage to restore after redirect
    const state: RedirectFlowState = {
      timestamp: Date.now(),
      returnUrl: window.location.href,
      nonce,
      kind: this.options.kind,
    };

    try {
      sessionStorage.setItem(REDIRECT_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      this.log("Failed to save state:", error);
      this.options.onError({
        code: "redirect_error",
        message: "Failed to start OAuth flow: could not save session state",
        details: { error },
      });
      return false;
    }

    // Redirect to OAuth URL (full page redirect)
    window.location.href = redirectTarget;
    return true;
  }

  /**
   * Check if returning from OAuth redirect and handle result
   * Call this on page load to detect OAuth return
   *
   * @returns true if OAuth return was detected and handled
   */
  static checkOAuthReturn(
    onSuccess: (grants: Grant[], completion: ConnectCompletion) => void,
    onError: (error: AlterError) => void,
  ): boolean {
    // Check if we have OAuth state saved
    const stateJson = sessionStorage.getItem(REDIRECT_STATE_KEY);
    if (!stateJson) {
      // No stored state. Usually this page simply was not part of a redirect
      // flow, and returning false is right.
      //
      // But if the URL carries this SDK's own callback parameters, a flow DID
      // come back here and its state is unreachable — and the overwhelmingly
      // likely cause is that it came back to a DIFFERENT ORIGIN than the one it
      // started on. `sessionStorage` is scoped to an origin by the browser, so
      // a `return_url` on another scheme/host/port can never see the state that
      // `startRedirectFlow` wrote; the backend accepts any HTTPS return URL and
      // does not know the launching origin, so nothing upstream can catch this.
      //
      // Silently returning false here is what made that a dead end: the user
      // lands back apparently signed in, nothing fires, and the developer has
      // no signal at all. Say so instead. (The same message covers the other
      // way to reach this state — the user cleared storage, or opened the
      // callback in a fresh tab — which has the same remedy: return to the
      // origin the flow began on.)
      if (hasCallbackParams()) {
        stripCallbackParams();
        onError({
          code: "redirect_state_missing",
          message:
            "Received an Alter Connect redirect callback, but no matching " +
            "flow state exists on this origin. The return URL must be on the " +
            "SAME origin (scheme, host and port) as the page that called " +
            "open() — browsers scope the flow's session storage to one origin, " +
            "so a callback delivered elsewhere cannot be completed.",
          details: { origin: window.location.origin },
        });
        return true;
      }
      return false;
    }

    try {
      // Validate the stored state field-by-field before ANY field is used. A
      // state we cannot validate can never be matched against a callback, so
      // it is dropped rather than partially trusted.
      const state = parseRedirectFlowState(stateJson);
      if (state === null) {
        sessionStorage.removeItem(REDIRECT_STATE_KEY);
        if (hasCallbackParams()) stripCallbackParams();
        return false;
      }

      // Check if this is a valid OAuth return (within 5 minutes). `timestamp`
      // is a proven finite number by this point — the previous `any`-typed read
      // made `elapsed` NaN for a non-numeric timestamp, and `NaN > MAX` is
      // `false`, so corrupt or forged state sailed THROUGH the freshness guard.
      // A negative elapsed (timestamp in the future) is just as invalid.
      const elapsed = Date.now() - state.timestamp;
      if (elapsed < 0 || elapsed > REDIRECT_STATE_MAX_AGE_MS) {
        // Stale state, clean up
        sessionStorage.removeItem(REDIRECT_STATE_KEY);
        if (hasCallbackParams()) stripCallbackParams();
        return false;
      }

      // Check URL for OAuth callback parameters
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get("alter_connect_success");
      const error = urlParams.get("alter_connect_error");

      if (success === null && error === null) {
        // No callback params on this load: the redirect has NOT come back yet
        // (client-side route change, a Back navigation, a reload mid-flow). The
        // entry is deliberately RETAINED so a callback that lands on a later
        // navigation still resolves against its own flow. The staleness window
        // is bounded to the 5-minute freshness check above — past it the entry
        // is dropped on the next call, so an abandoned flow can never be
        // resumed by an unrelated navigation.
        return false;
      }

      // BIND the callback to this flow. Every grant field below (`grant_id`,
      // `provider`, `account_identifier`, `scopes`, `status`) arrives as a
      // plain query parameter, so without this check ANY link of the form
      // `https://app.example/?alter_connect_success=true&grant_id=…` drives
      // `onSuccess` with attacker-chosen grant data whenever a flow's state
      // happens to be pending — a spoofed "connected!" state in the host app.
      // The nonce was minted by `startRedirectFlow`, sent to the Connect UI in
      // the query string, and must come back on the callback.
      if (urlParams.get(REDIRECT_NONCE_PARAM) !== state.nonce) {
        console.warn(
          "[OAuth Handler] Ignoring OAuth callback parameters that do not " +
            "carry this flow's nonce",
        );
        stripCallbackParams();
        return false;
      }

      if (success === "true") {
        // Validate required fields
        const grantId = urlParams.get("grant_id");
        const provider = urlParams.get("provider");
        const accountIdentifier = urlParams.get("account_identifier");

        if (!grantId || !provider) {
          cleanupRedirectState(state.returnUrl);
          onError({
            code: "invalid_response",
            message: "OAuth redirect returned incomplete grant data",
          });
          return true;
        }

        const grant: Grant = {
          grant_id: grantId,
          provider: provider,
          provider_name: urlParams.get("provider_name") || provider,
          account_identifier: accountIdentifier,
          timestamp: urlParams.get("timestamp") || new Date().toISOString(),
          operation: parseOperation(urlParams.get("operation")),
          scopes: urlParams.get("scopes")?.split(",") || [],
          status: parseStatus(urlParams.get("status")),
        };
        // The delegation to the session's agent. The page writes
        // `delegated_agent_id` and `delegable` together, or neither, and
        // `delegation_policy_refused` with them (a page that predates it
        // omits it, which reads as `false`). With `delegated_agent_id`
        // present, anything partial or malformed is refused as the popup path
        // and both server SDKs refuse it. Without it the session named no
        // agent, and a `delegable` on the URL is the application's own.
        const delegation = parseRedirectDelegation(urlParams);
        if (delegation === "invalid") {
          cleanupRedirectState(state.returnUrl);
          onError({
            code: "invalid_response",
            message: "OAuth redirect returned malformed delegation data",
          });
          return true;
        }
        if (delegation !== null) grant.delegation = delegation;

        const grants = [grant];
        let completion: ConnectCompletion = {
          kind: "oauth",
          grants,
          failedGrants: [],
        };
        if (state.kind === "managed_secret") {
          const managedSecret = parseRedirectManagedSecret(urlParams);
          if (managedSecret === null) {
            cleanupRedirectState(state.returnUrl);
            onError({
              code: "invalid_response",
              message:
                "Connect redirect returned malformed managed-secret completion data",
            });
            return true;
          }
          completion = {
            kind: "managed_secret",
            grants,
            failedGrants: [],
            managedSecret,
          };
        }
        cleanupRedirectState(state.returnUrl);
        onSuccess(grants, completion);
        return true;
      }

      if (error) {
        const alterError: AlterError = {
          code: urlParams.get("error_code") || "oauth_error",
          message:
            urlParams.get("error_description") || "OAuth authorization failed",
        };

        cleanupRedirectState(state.returnUrl);
        onError(alterError);
        return true;
      }

      // A callback param was present and nonce-bound, but it was neither a
      // recognized success nor a non-empty error — nothing to report.
      cleanupRedirectState(state.returnUrl);
      return false;
    } catch (err) {
      console.error("[OAuth Handler] Failed to check OAuth return:", err);
      sessionStorage.removeItem(REDIRECT_STATE_KEY);
      if (hasCallbackParams()) stripCallbackParams();
      return false;
    }
  }

  /**
   * Open OAuth popup (for desktop)
   */
  private createPopup(url: string): Window | null {
    // Calculate popup position (centered)
    const left =
      window.screenX + (window.outerWidth - this.options.popupWidth) / 2;
    const top =
      window.screenY + (window.outerHeight - this.options.popupHeight) / 2;

    const features = [
      `width=${this.options.popupWidth}`,
      `height=${this.options.popupHeight}`,
      `left=${left}`,
      `top=${top}`,
      "resizable=yes",
      "scrollbars=yes",
      "status=yes",
    ].join(",");

    this.log("Opening OAuth popup:", url);
    return window.open(url, "alter_oauth_popup", features);
  }

  /**
   * Open the popup flow, reporting a blocked window as `popup_blocked`.
   *
   * The open sequence itself (origin derivation, window creation, close
   * polling, the message listener) is `tryOpenWindow`'s; this method only adds
   * the error the popup flow owes the application when the window is refused.
   *
   * @returns `true` when the window opened, `false` when the flow was refused
   *   (the error callback has already fired).
   */
  openPopup(url: string): boolean {
    // Derive first so an unparseable URL reports `invalid_oauth_url` once,
    // rather than that error followed by a spurious `popup_blocked`.
    if (!this.deriveExpectedOrigin(url)) {
      return false;
    }
    if (this.tryOpenWindow(url)) {
      return true;
    }
    this.options.onError({
      code: "popup_blocked",
      message:
        "Popup was blocked by browser. Please allow popups for this site.",
    });
    return false;
  }

  /**
   * Close popup and clean up
   */
  close(): void {
    this.log("Closing OAuth handler");

    // Close popup
    if (this.popup && !this.popup.closed) {
      this.popup.close();
    }
    this.popup = null;

    // Stop polling
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    if (this.closedGraceTimer !== null) {
      clearTimeout(this.closedGraceTimer);
      this.closedGraceTimer = null;
    }

    // Remove message listener
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
      this.messageListener = null;
    }
  }

  /**
   * Poll to detect when popup is closed.
   *
   * A closed popup is a cancel only if no outcome follows it within
   * ``POPUP_CLOSED_GRACE_MS``. The hosted page posts its outcome and then
   * closes itself at once (the single-provider fast path has no screen to
   * read), and the popup runs in another process: its ``closed`` flag can flip
   * before the posted message is dispatched here. Settling on the first
   * ``closed`` read reported a completed connection as an exit and dropped its
   * success.
   */
  private startPolling(): void {
    this.pollInterval = window.setInterval(() => {
      if (this.settled || this.closedGraceTimer !== null) return;
      if (!this.popup || this.popup.closed) {
        this.closedGraceTimer = window.setTimeout(() => {
          this.closedGraceTimer = null;
          if (this.settled) return;
          this.log("Popup closed by user");
          this.settled = true;
          this.close();
          this.options.onCancel();
        }, POPUP_CLOSED_GRACE_MS);
      }
    }, POPUP_POLL_INTERVAL_MS);
  }

  /**
   * Listen for postMessage from OAuth callback page
   */
  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      // Ignore if already settled (prevents race with polling)
      if (this.settled) return;

      // Validate origin against the expected backend origin
      if (event.origin !== this.expectedOrigin) {
        this.log(
          "Rejected message from unexpected origin:",
          event.origin,
          "(expected:",
          this.expectedOrigin + ")",
        );
        return;
      }

      // Origin alone is not identity: only the window THIS handler opened may
      // settle this flow. An inline widget on the same page relays its own
      // outcome to `window.parent` from the very same backend origin, and a
      // second popup widget posts from that origin too, so an origin-only
      // check let either of them settle (and close) an unrelated popup flow.
      // The WindowProxy `window.open` returned keeps its identity across the
      // popup's navigations, so the comparison still holds after the provider
      // round-trip.
      if (!this.popup || event.source !== this.popup) {
        this.log("Rejected message from a window this handler did not open");
        return;
      }

      this.log("Received message from", event.origin);

      const outcome = parseConnectMessage(event.data, this.options.kind);
      switch (outcome.kind) {
        case "ignore":
        case "ready":
          // `ready` is the inline frame's load proof; a popup has no load
          // timer, so there is nothing to do with it here.
          return;
        case "notice":
          this.log("Connect notice:", outcome.event);
          // DELIBERATELY does not set `settled` and does not call `close()`:
          // the hosted flow is still live and the user can still finish it.
          // Routing this through onError instead would close the popup and
          // strand a user who merely refused one of several providers.
          this.options.onNotice(outcome.event, outcome.metadata);
          return;
        case "exit":
          // The hosted page asked to end the flow without an outcome (the
          // inline widget's Cancel; a popup normally just closes). Settle
          // first so the closed-window poll cannot fire a second exit.
          this.log("Connect exit");
          this.settled = true;
          this.close();
          this.options.onCancel();
          return;
        case "success":
          this.log("Connect completion:", outcome.grants.length, "grants");
          this.settled = true;
          this.close();
          this.options.onSuccess(outcome.grants, outcome.completion);
          return;
        case "error":
          this.log("OAuth error");
          this.settled = true;
          this.close();
          this.options.onError(outcome.error);
          return;
      }
    };

    window.addEventListener("message", this.messageListener);
  }

  /**
   * Log debug message
   */
  private log(...args: unknown[]): void {
    if (this.options.debug) {
      // Deliberate console.log: this IS the debug logger, and it only fires
      // when the integrator explicitly opts in via ``debug``.
      // eslint-disable-next-line no-console
      console.log("[OAuth Handler]", ...args);
    }
  }
}
